import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { getMuscleGroups, getWorkoutTemplate, searchExercises, updateWorkoutTemplate, createWorkoutTemplate } from '../api';
import '../styles/workout-builder.css'; // Import the new CSS file

const WorkoutTemplateBuilder = () => {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const [templateName, setTemplateName] = useState('');
    const [description, setDescription] = useState('');
    const [difficultyLevel, setDifficultyLevel] = useState('Beginner');
    const [muscleGroups, setMuscleGroups] = useState([]);
    const [days, setDays] = useState({
        Monday: { title: 'Monday Workout', targetMuscles: [] },
        Tuesday: { title: 'Tuesday Workout', targetMuscles: [] },
        Wednesday: { title: 'Wednesday Workout', targetMuscles: [] },
        Thursday: { title: 'Thursday Workout', targetMuscles: [] },
        Friday: { title: 'Friday Workout', targetMuscles: [] },
        Saturday: { title: 'Saturday Workout', targetMuscles: [] },
        Sunday: { title: 'Sunday Workout', targetMuscles: [] }
    });
    const [activeDay, setActiveDay] = useState('Monday'); // State to manage active day in sidebar
    const [exercises, setExercises] = useState({ // Exercises organized by day
        Monday: [],
        Tuesday: [],
        Wednesday: [],
        Thursday: [],
        Friday: [],
        Saturday: [],
        Sunday: []
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    useEffect(() => {
        const fetchMuscleGroups = async () => {
            try {
                const response = await getMuscleGroups();
                setMuscleGroups(response.data);
            } catch (error) {
                console.error('Failed to fetch muscle groups', error);
            }
        };
        fetchMuscleGroups();
    }, []);

    useEffect(() => {
        if (templateId) {
            const fetchTemplate = async () => {
                try {
                    const response = await getWorkoutTemplate(templateId);
                    const template = response.data;
                    setTemplateName(template.template_name);
                    setDescription(template.description);
                    setDifficultyLevel(template.difficulty_level);
                    
                    const fetchedDays = {};
                    const fetchedExercises = { Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: [], Sunday: [] };

                    Object.keys(days).forEach(day => {
                        fetchedDays[day] = {
                            title: template.days && template.days[day] ? template.days[day].title : `${day} Workout`,
                            targetMuscles: template.days && template.days[day] ? template.days[day].targetMuscles : []
                        };
                    });

                    if (template.exercises) {
                        if (Array.isArray(template.exercises)) {
                            template.exercises.forEach(exercise => {
                                if (fetchedExercises[exercise.day_of_week]) {
                                    fetchedExercises[exercise.day_of_week].push(exercise);
                                }
                            });
                        } else {
                            // If it's an object (grouped by day)
                            Object.keys(template.exercises).forEach(day => {
                                if (fetchedExercises[day]) {
                                    fetchedExercises[day] = template.exercises[day];
                                }
                            });
                        }
                    }
                    setDays(fetchedDays);
                    setExercises(fetchedExercises);

                } catch (error) {
                    console.error('Failed to fetch workout template', error);
                }
            };
            fetchTemplate();
        }
    }, [templateId]);

    useEffect(() => {
        const search = async () => {
            const muscleGroupsQuery = days[activeDay].targetMuscles.join(',');

            if (searchTerm || muscleGroupsQuery) {
                const response = await searchExercises(searchTerm, muscleGroupsQuery);
                setSearchResults(response.data);
            } else {
                setSearchResults([]);
            }
        };
        const debounce = setTimeout(search, 300);
        return () => clearTimeout(debounce);
    }, [searchTerm, days, activeDay]);

    const handleOnDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination) return;

        const sourceDay = source.droppableId;
        const destDay = destination.droppableId;
        const sourceExercises = [...exercises[sourceDay]];
        const [removed] = sourceExercises.splice(source.index, 1);

        if (sourceDay === destDay) {
            sourceExercises.splice(destination.index, 0, removed);
            setExercises({
                ...exercises,
                [sourceDay]: sourceExercises
            });
        } else {
            const destExercises = [...exercises[destDay]];
            destExercises.splice(destination.index, 0, removed);
            setExercises({
                ...exercises,
                [sourceDay]: sourceExercises,
                [destDay]: destExercises
            });
        }
    };

    const addExerciseToDay = (exercise, day) => {
        setExercises({
            ...exercises,
            [day]: [...exercises[day], { ...exercise, sets: 3, reps: '12', weight: '10kg', rest_time: '60s' }]
        });
    };

    const handleExerciseChange = (day, index, field, value) => {
        const newExercises = { ...exercises };
        newExercises[day][index][field] = value;
        setExercises(newExercises);
    };
    
    const removeExercise = (day, index) => {
        const newExercises = { ...exercises };
        newExercises[day].splice(index, 1);
        setExercises(newExercises);
    };

    const saveTemplate = async () => {
        const allExercises = Object.entries(exercises).flatMap(([day, dayExercises]) => 
            dayExercises.map((exercise, index) => ({
                exercise_id: exercise.exercise_id,
                day_of_week: day,
                sets: exercise.sets,
                reps: exercise.reps,
                weight: exercise.weight,
                rest_time: exercise.rest_time,
                order_sequence: index + 1
            }))
        );

        const templateData = {
            template_name: templateName,
            description: description,
            difficulty_level: difficultyLevel,
            days: days, // Include day-specific settings
            exercises: allExercises
        };

        try {
            if (templateId) {
                await updateWorkoutTemplate(templateId, templateData);
                alert('Workout template updated successfully!');
            } else {
                await createWorkoutTemplate(templateData);
                alert('Workout template created successfully!');
            }
            navigate('/workout-templates');
        } catch (error) {
            console.error('Failed to save workout template', error);
            alert('Failed to save workout template.');
        }
    };

    const handleTargetMusclesChange = (muscle) => {
        const currentMuscles = days[activeDay].targetMuscles;
        const newMuscles = currentMuscles.includes(muscle)
            ? currentMuscles.filter(m => m !== muscle)
            : [...currentMuscles, muscle];
        setDays(prev => ({ ...prev, [activeDay]: { ...prev[activeDay], targetMuscles: newMuscles } }));
    };

    return (
        <div className="page-container">
            {/* SECTION 1: Header */}
            <header className="template-header">
                <div className="header-top">
                    <h1>{templateId ? 'Edit Workout Template' : 'Create Workout Template'}</h1>
                    <div className="header-actions">
                        <button className="btn btn-secondary" onClick={() => navigate('/workout-templates')}>Cancel</button>
                        <button className="btn btn-primary" onClick={saveTemplate}>Save Template</button>
                    </div>
                </div>
                <div className="header-details">
                    <div className="form-group">
                        <label htmlFor="template-name">Template Name</label>
                        <input type="text" id="template-name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label htmlFor="description">Description</label>
                        <input type="text" id="description" value={description} onChange={(e) => setDescription(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label htmlFor="difficulty-level">Difficulty Level</label>
                        <select id="difficulty-level" value={difficultyLevel} onChange={(e) => setDifficultyLevel(e.target.value)}>
                            <option>Beginner</option>
                            <option>Intermediate</option>
                            <option>Advanced</option>
                        </select>
                    </div>
                </div>
            </header>

            <div className="builder-layout">
                {/* SECTION 2: Sidebar */}
                <aside className="sidebar">
                    <h2>Exercise Library</h2>
                    <div className="form-group">
                        <input type="text" placeholder="Search Exercises" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    <div className="exercise-library-results">
                        {searchResults.map(exercise => (
                            <div key={exercise.exercise_id} className="exercise-library-item">
                                <p className="font-semibold">{exercise.name}</p>
                                <button onClick={() => addExerciseToDay(exercise, activeDay)} className="btn btn-primary btn-sm">Add to {activeDay.substring(0,3)}</button>
                            </div>
                        ))}
                    </div>

                    <h2 className="mt-8">Week Plan</h2>
                    <nav>
                        {Object.keys(days).map(day => (
                            <a 
                                key={day}
                                href="#" 
                                className={`day-nav-link ${activeDay === day ? 'active' : ''}`}
                                onClick={(e) => { e.preventDefault(); setActiveDay(day); }}
                            >
                                <strong>{day}</strong>
                                <span>{days[day].title}</span>
                            </a>
                        ))}
                    </nav>
                </aside>

                {/* SECTION 3: Main Content (Day Editor) */}
                <main className="main-content">
                    <div className="day-editor">
                        <div className="day-settings">
                            <div className="form-group">
                                <label htmlFor="day-title">Day Title / Focus</label>
                                <input 
                                    type="text" 
                                    id="day-title" 
                                    value={days[activeDay].title} 
                                    onChange={(e) => setDays(prev => ({ ...prev, [activeDay]: { ...prev[activeDay], title: e.target.value } }))} 
                                />
                            </div>
                            <div className="form-group">
                                <label>Target Muscles</label>
                                <div className="muscle-select-container">
                                    {muscleGroups.map(group => (
                                        <div 
                                            key={group} 
                                            className={`muscle-tag ${days[activeDay].targetMuscles.includes(group) ? 'selected' : ''}`}
                                            onClick={() => handleTargetMusclesChange(group)}
                                        >
                                            {group}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        <div className="exercise-list">
                            <DragDropContext onDragEnd={handleOnDragEnd}>
                                <Droppable droppableId={activeDay}>
                                    {(provided) => (
                                        <div {...provided.droppableProps} ref={provided.innerRef} className="min-h-[100px]">
                                            {exercises[activeDay].map((exercise, index) => (
                                                <Draggable key={`${activeDay}-${exercise.exercise_id}-${index}`} draggableId={`${activeDay}-${exercise.exercise_id}-${index}`} index={index}>
                                                    {(provided) => (
                                                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="exercise-card">
                                                            <div className="card-header">
                                                                <span className="drag-handle">⠿</span>
                                                                <h4>{exercise.name}</h4>
                                                                <div className="card-actions">
                                                                    <button title="Delete" onClick={() => removeExercise(activeDay, index)}>🗑️</button>
                                                                </div>
                                                            </div>
                                                            <div className="card-body">
                                                                <div className="form-group">
                                                                    <label>Sets</label>
                                                                    <input type="text" value={exercise.sets} onChange={(e) => handleExerciseChange(activeDay, index, 'sets', e.target.value)} />
                                                                </div>
                                                                <div className="form-group">
                                                                    <label>Reps</label>
                                                                    <input type="text" value={exercise.reps} onChange={(e) => handleExerciseChange(activeDay, index, 'reps', e.target.value)} />
                                                                </div>
                                                                <div className="form-group">
                                                                    <label>Weight</label>
                                                                    <input type="text" value={exercise.weight} onChange={(e) => handleExerciseChange(activeDay, index, 'weight', e.target.value)} />
                                                                </div>
                                                                <div className="form-group">
                                                                    <label>Rest</label>
                                                                    <input type="text" value={exercise.rest_time} onChange={(e) => handleExerciseChange(activeDay, index, 'rest_time', e.target.value)} />
                                                                </div>
                                                                <div className="form-group full-width">
                                                                    <label>Video Link</label>
                                                                    <input type="text" placeholder="https://youtube.com/watch?v=..." value={exercise.video_url || ''} onChange={(e) => handleExerciseChange(activeDay, index, 'video_url', e.target.value)} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </div>
                        
                        
                    </div>
                </main>
            </div>
        </div>
    );
};

export default WorkoutTemplateBuilder;